import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion, useInView, AnimatePresence } from "framer-motion";

const features = [
  {
    title: "Real-Time Order Management",
    icon: (
      <img
        src="/assets/features/pos/icon1.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Manage dine-in, takeaway, and delivery orders from a central dashboard.",
      "Create, update, and assign orders to tables or customer profiles.",
      "Track order progress in real time.",
      "Sync directly with the Kitchen Display System to keep kitchen teams in the loop.",
    ],
    image: "/assets/features/pos/pos1.svg",
  },
  {
    title: "Flexible Payments",
    icon: (
      <img
        src="/assets/features/pos/icon2.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Accept Raast QR payments (static and dynamic).",
      "Integrate with major card terminals.",
      "Allow split payments using multiple methods.",
      "Manage cash transactions with automatic change calculation.",
    ],
    image: "/assets/features/pos/pos2.svg",
  },
  {
    title: (
      <>
        Customisable Menu <br /> and Modifiers
      </>
    ),
    icon: (
      <img
        src="/assets/features/pos/icon3.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Add unlimited menu items with descriptions, images, and prices.",
      "Create item variations such as size, flavor, or extras.",
      "Use custom modifiers for special customer requests.",
      "Set up different menus based on time of day, such as breakfast, lunch, and dinner.",
    ],
    image: "/assets/features/pos/pos3.svg",
  },
  {
    title: (
      <>
        Table <br /> Management
      </>
    ),
    icon: (
      <img
        src="/assets/features/pos/icon4.svg"
        alt="My Icon"
        style={{ width: 60, height: 60 }}
      />
    ),
    points: [
      "Use a visual floor plan to manage seating and table assignments.",
      "Merge, split, or transfer tables during service.",
      "Track table status in real time to improve flow and reduce wait times.",
    ],
    image: "/assets/features/pos/pos4.svg",
  },
];

export default function KeyFeaturesCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const currentFeature = features[currentSlide];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 }); // triggers once when 20% visible

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? features.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev === features.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      ref={ref}
      sx={{
        backgroundColor: "#F08203",
        color: "#fff",
        borderRadius: 3,
        mx: 2,
        py: 6,
        mt: 4,
        position: "relative",
      }}
    >
      <Typography
        variant="h1"
        fontWeight="bold"
        gutterBottom
        sx={{ px: { xs: 2, sm: 6, lg: 10 } }}
      >
        Key Features
      </Typography>

      <Divider sx={{ backgroundColor: "#fff", mb: 2 }} />

      <Grid
        container
        spacing={4}
        alignItems="center"
        sx={{ px: { xs: 2, sm: 6, lg: 10 } }}
      >
        <AnimatePresence mode="wait">
          {isInView && (
            <>
              <Grid
                item
                xs={12}
                md={7}
                key={currentSlide}
                component={motion.div}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.6 }}
              >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <Box>{currentFeature.icon}</Box>
                  <Typography variant="h5" fontWeight={600}>
                    {currentFeature.title}
                  </Typography>
                </Stack>

                <Stack spacing={2} pl={2}>
                  {currentFeature.points.map((point, index) => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      key={index}
                    >
                      <CheckCircleIcon fontSize="small" />
                      <Typography>{point}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              <Grid
                item
                xs={12}
                md={5}
                key={"image-" + currentSlide}
                component={motion.div}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  component="img"
                  src={currentFeature.image}
                  alt={currentFeature.title}
                  sx={{ width: "100%" }}
                />
              </Grid>
            </>
          )}
        </AnimatePresence>
      </Grid>

      {/* Arrows */}
      <Box sx={{ px: { xs: 2, sm: 6, lg: 10 }, position: "relative" }}>
        <Divider
          sx={{
            backgroundColor: "#fff",
            position: "absolute",
            top: "50%",
            left: { xs: 20, sm: 40 },
            right: { xs: 20, sm: 40 },
            zIndex: 0,
          }}
        />

        <Stack
          direction="row"
          spacing={2}
          mt={4}
          justifyContent={{ xs: "center", md: "center", lg: "flex-start" }}
          sx={{ position: "relative", zIndex: 1, ml: { xs: 0, sm: 4 } }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={currentSlide === 0}
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              px: 2,
              py: 1,
              borderRadius: "999px",
              "&:hover": { backgroundColor: "#eee" },
              "&.Mui-disabled": {
                backgroundColor: "#fff",
                color: "#ccc",
                opacity: 1,
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={currentSlide === features.length - 1}
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              px: 2,
              py: 1,
              borderRadius: "999px",
              "&:hover": { backgroundColor: "#eee" },
              "&.Mui-disabled": {
                backgroundColor: "#fff",
                color: "#ccc",
                opacity: 1,
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
